import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import TextLink from '@/components/ui/TextLink'
import TitleRow from '@/components/ui/TitleRow'
import { useSuspenseFundDividends } from '@/features/mutual-funds/api/queries'
import { fundLinkOptions } from '@/features/mutual-funds/lib/links'
import { toChartNumber } from '@/lib/money'
import type { OpenEndedDividend } from '@/schemas/mutual-funds'
import DistributableDividendsCard from '@/screens/mutual-funds/components/dividends/DistributableDividendsCard'
import OpenEndedDividendTable from '@/screens/mutual-funds/components/dividends/OpenEndedDividendTable'
import { loadDividends } from '@/screens/mutual-funds/DividendsScreen.loader'

const TITLE = 'Dividends'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title={TITLE} />

/**
 * The typical count of open-ended funds that have paid (2 of 7 today) — a data-dependent count,
 * per docs/route-loading.md's Skeletons section.
 */
const PAID_SKELETON_ROWS = 2

/**
 * Built from the loaded page's own boxes, in the order the loaded page draws them, 24 apart.
 *
 * The distributable note: the mascot beside its one line — the list is EMPTY on dev today, so
 * the note is the shape this skeleton is built for. Then the open-ended block: its head row (the
 * 16 title, the count at the right), 12 over a three-column `DataTableSkeleton` (Fund, Total
 * dividend, Each year) at the typical paid count, and the "No dividend yet" line.
 */
function DividendsPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading dividends…" header={HEADER} pageTitle={TITLE}>
      <div className="flex items-center gap-3">
        <Skeleton className="size-14 rounded-full" />
        <Skeleton className="h-4 w-80 rounded-xs" />
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex h-6 items-center justify-between gap-3">
          <Skeleton className="h-4 w-64 rounded-xs" />
          <Skeleton className="h-3 w-32 rounded-xs" />
        </div>
        <DataTableSkeleton rows={PAID_SKELETON_ROWS} columns={3} rowHeight="lg" />
        <Skeleton className="mt-1 h-4 w-72 rounded-xs" />
      </div>
    </ScreenLayout>
  )
}

function DividendsError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Dividends did not load" />
}

export const Route = createFileRoute('/_app/mutual-funds/dividends')({
  loader: loadDividends,
  pendingComponent: DividendsPending,
  errorComponent: DividendsError,
  component: DividendsScreen,
})

/**
 * Mutual funds · Dividends — two independent reads from one payload
 * (`/dividends/`, no parameters, no search params): declared-but-unpaid
 * dividends for close-ended funds, and open-ended funds' all-time/per-year
 * dividend record.
 *
 * The 2026-11-03 mock's flow: the distributable list (or its one-line note when none is
 * declared), then "Dividends paid by open-ended funds" with how many have paid, 12 over a table
 * of only those, and the ones still at 0% as a line of links under it. Every fund opens its own
 * page. The table draws its own panel, so it is not wrapped in a second card.
 */
/** A fund that has paid something: `"0.00"` is a real "declared none yet", never a gap. */
const hasPaid = (row: OpenEndedDividend): boolean =>
  row.totalPercent !== null && toChartNumber(row.totalPercent) > 0

function DividendsScreen() {
  const { data } = useSuspenseFundDividends()
  const paid = data.openEnded.filter(hasPaid)
  const unpaid = data.openEnded.filter((row) => !hasPaid(row))

  return (
    <ScreenLayout header={HEADER} pageTitle={TITLE}>
      <DistributableDividendsCard rows={data.distributable} />
      <section aria-labelledby="open-ended-dividends" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 id="open-ended-dividends" className="text-h-m">
            Dividends paid by open-ended funds
          </h2>
          {data.openEnded.length === 0 ? null : (
            <p className="text-body-xs text-muted-foreground-subtle">
              {paid.length} of {data.openEnded.length} funds have paid
            </p>
          )}
        </div>
        <OpenEndedDividendTable rows={paid} />
        {unpaid.length === 0 ? null : (
          <p className="mt-1 text-body-m text-muted-foreground">
            <span className="text-muted-foreground-subtle">No dividend yet:</span>{' '}
            {unpaid.map((row, index) => (
              <span key={row.symbol}>
                {index > 0 ? ', ' : null}
                <TextLink {...fundLinkOptions(row.symbol)} aria-label={row.name ?? row.symbol}>
                  {row.symbol}
                </TextLink>
              </span>
            ))}
          </p>
        )}
      </section>
    </ScreenLayout>
  )
}
